<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.auth.actions.changePin.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.save'), type: 'confirm', callback: confirm },
    ]"
    :portal="portal"
  >
    <template #heading>
      <img
        v-if="account.employee.image"
        :src="account.employee.image"
        class="mx-auto h-12 w-12 rounded-full mt-3"
      >
    </template>

    <div class="pt-4 text-left">
      <o-form ref="form">
        <segment-field
          v-model="none"
          :options="[
            { label: $t('auth.pin.change'), value: false },
            { label: $t('auth.pin.remove'), value: true }
          ]"
          class="block mb-6"
        />

        <o-field-group v-slot="{ status }" :label="$t('auth.password.label')" :name="$t('auth.password.label')" rules="required" class="block pb-3">
          <password-field v-model="password" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="!none"
          v-slot="{ status }"
          :label="$t('auth.pin.new')"
          name="pin"
          rules="integer|required|pin:@confirm"
          class="block pb-3"
        >
          <password-field v-model="newPin" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="!none"
          v-slot="{ status }"
          :label="$t('auth.pin.confirm')"
          name="confirm"
          rules="integer|required"
          class="block pb-3"
        >
          <password-field v-model="confirmPin" :status="status" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  PasswordField,
  ToggleField,
  SegmentField,
} from '@/components/fields'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $auth, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const none = ref(false)
const password = ref('')
const newPin = ref('')
const confirmPin = ref('')

const account = computed(() => $auth.info)

const info = computed(() => {
  const employee = account.value?.employee
  return `${employee?.firstName} ${employee?.lastName}`
})

const open = () => {
  none.value = !account.value?.employee?.pinHash
  password.value = ''
  newPin.value = ''
  confirmPin.value = ''

  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const data = {
      password: password.value,
      newPin: none.value ? null : newPin.value,
      confirmPin: none.value ? null : confirmPin.value,
    }

    const success = await $api.auth.changePin(data)
    emit('update:loading', false)

    if (success) {
      $notification.toast({
        title: $i18n.t('auth.pin.changeSuccess'),
        text: $i18n.t('components.auth.actions.changePin.successInfo'),
        variant: 'success',
      })

      emit('success')

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    emit('error')
  }
}

defineExpose({ open })
</script>
